<script setup>
import textHelpers from "../functions/text";
</script>

<template>
  <MetaInfo :title="meta.title" :description="meta.description"></MetaInfo>

  <navigation
    announcementText="Let AI bring your email content to life."
    :announcementLink="{
      url: '/features/ai-content-assistant',
      text: 'Try our new AI Content Assistant',
    }"
    announcementIcon="ai-outline-dark"
  ></navigation>

  <PageHeader
    title="The easy to use<br/>marketing platform"
    subtitle-text="If you know marketing matters but aren’t sure where to start, Transpond helps you take the next step with confidence."
  >
    <PageHeaderRating
      button-text="Get started"
      rating-text=""
    ></PageHeaderRating>
  </PageHeader>

  <div class="homepage-hero__image-container hero-fade-in hero-fade-in--delay-3">
    <img
      src="../assets/images/home/hero-weiss-moller.webp"
      alt="Email Editor Screenshot"
      class="email-editor-screenshot"
    />
  </div>

  <!-- Why Transpond Section -->
  <div class="why-transpond-section">
    <div class="why-transpond-container">
      <h3 class="mb-8">Start marketing today, not tomorrow</h3>
      
        <div class="why-transpond-grid">
          <div class="why-transpond-card">
            <img src="../assets/why-transpond_capsule.png" alt="Your contacts, always in sync" class="why-transpond-card__image" />
            <h5 class="mb-2">Your contacts, always in sync</h5>
            <p class="why-transpond-description">Built by the team behind Capsule CRM, making it easy to start marketing with your existing CRM data.</p>
          </div>
          
          <div class="why-transpond-card">
            <img src="../assets/why-transpond_brand.png" alt="Get started now" class="why-transpond-card__image" />
            <h5 class="mb-2">Get started now</h5>
            <p class="why-transpond-description">Use branded templates or let AI write your first campaign. You'll be sending emails before your coffee gets cold.</p>
          </div>
          
          <div class="why-transpond-card">
            <img src="../assets/why-transpond_mailchimp.png" alt="Cheaper than Mailchimp" class="why-transpond-card__image" />
            <h5 class="mb-2">Cheaper than Mailchimp</h5>
            <p class="why-transpond-description">Similar capabilities, way better value. We'll even migrate your existing Mailchimp data.</p>
          </div>
        </div>
    </div>
  </div>

  <!-- How Transpond Works Section -->
  <div class="how-it-works-container" data-gtm-context="How It Works">

    <div class="divider--dashed"></div>
    
    <!-- Build your audience -->
    <div class="step-section">
      <div class="step-header">
        <h3 class="step-title">Grow your Audience</h3>
      </div>
      <h4 class="step-headline">Turn visitors into contacts</h4>
      
      <div class="bento-grid">
        <router-link to="/features/signupforms/" class="bento-card bento-card--half">
          <h5>Collect sign-ups you can follow up with</h5>
          <p>Use simple forms on your site or a ready-made landing page - everyone who signs up is saved automatically, with no manual work.</p>
          <img src="../assets/homepage-signup_forms.png" alt="Signup forms" class="bento-card__image" />
        </router-link>
          <router-link to="/features/social-campaigns/" class="bento-card bento-card--half">
            <h5>Expand your reach on social media</h5>
            <p>Schedule posts to Facebook, Instagram, LinkedIn, and X in one place. Share updates, connect with new people, and win more business.</p>
            <img src="../assets/homepage-social-campaigns.png" alt="Social campaigns" class="bento-card__image" />
          </router-link>
      </div>
    </div>

    <div class="divider--dashed"></div>

    <!-- Drive engagement -->
    <div class="step-section">
      <div class="step-header">
        <h3 class="step-title">Drive Engagement</h3>
      </div>
      <h4 class="step-headline">Stay in touch without having to remember</h4>
      
      <div class="bento-grid">
        <router-link to="/features/campaignbuilder/" class="bento-card bento-card--half">
          <h5>Create campaigns that connect</h5>
          <p>Send newsletters, promotions, and updates to your entire list or specific groups. Create emails that look great and work across devices.</p>
          <img src="../assets/homepage-campaigns.png" alt="Campaigns" class="bento-card__image" />
        </router-link>
         <router-link to="/features/automations/" class="bento-card bento-card--half">
           <h5>Automate the busy work</h5>
           <p>Set up simple rules once: "Send this when someone signs up," "Follow up in 3 days," Then they happen automatically while you focus on work.</p>
           <img src="../assets/homepage-automations.png" alt="Automations" class="bento-card__image" />
          </router-link>
        <router-link to="/features/ai-content-assistant/" class="bento-card">
          <h5>Never start from a blank page</h5>
          <p>Choose from templates that match your brand, or let our AI Assistant help you write messages that sound like you.</p>
          <img src="../assets/homepage-brand.png" alt="Transactional emails" class="bento-card__image" />
        </router-link>
        <router-link to="/features/sms/" class="bento-card">
          <h5>Add other channels when you're ready</h5>
          <p>Start with email, then add SMS when it makes sense for your business - all from one place.</p>
          <img src="../assets/homepage-sms.png" alt="SMS" class="bento-card__image" />
        </router-link>
        <router-link to="/features/analytics/" class="bento-card">
          <h5>See what people respond to</h5>
          <p>Quickly understand which messages are being opened, clicked, and replied to - without digging through reports.</p>
          <img src="../assets/homepage-analytics.png" alt="Analytics" class="bento-card__image" />
        </router-link>
      </div>
    </div>

    <div class="divider--dashed"></div>

    <!-- Manage conversations -->
    <div class="step-section">
      <div class="step-header">
        <h3 class="step-title">Manage Conversations</h3>
      </div>
      <h4 class="step-headline">Be there when it matters</h4>
      
        <div class="bento-grid">
          <router-link to="/features/conversations/" class="bento-card bento-card--half">
            <h5>Keep conversations in one place</h5>
            <p>When customers reply, everything lands together - so you can respond quickly, without switching tools or losing context.</p>
            <img src="../assets/homepage-conversations.png" alt="Conversations" class="bento-card__image" />
          </router-link>
          <router-link to="/features/conversations/" class="bento-card bento-card--half">
            <h5>Be available when people reach out</h5>
            <p>Respond in real time and let common questions be handled automatically when you're not around.</p>
            <img src="../assets/homepage-live-chat.png" alt="Live chat" class="bento-card__image" />
          </router-link>
        </div>
    </div>
  </div>

  <div class="divider--dashed"></div>

  <div class="integrations-section">
    <div class="integrations-container">
      <section class="integrations-showcase">
        <div class="integrations-showcase__header">
          <h2 class="section-title" style="font-size: 36px;">
            Works with the tools you already use
          </h2>
          <a href="/integrations/" class="btn btn--primary btn--sm">
            View all integrations
          </a>
        </div>

        <div class="integrations-grid">
          <div
            class="integration-card"
            v-for="integration in integrations"
            :key="integration.name"
          >
            <router-link :to="'/integrations/' + integration.slug + '/'">
              <img
                :src="integration.icon"
                :alt="integration.name + ' icon'"
                class="integration-icon"
              />
              <h3>{{ integration.name }}</h3>
              <p>{{ integration.description }}</p>
            </router-link>
          </div>
        </div>
      </section>
    </div>
  </div>

  <FooterCTA
    title="The all-in-one marketing platform"
    buttonLink="Pricing"
    buttonText="Get started free"
  />

  <site-footer></site-footer>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import PageHeader from "@/components/Header/PageHeader";
import PageHeaderRating from "@/components/Header/PageHeaderRating";
import SiteFooter from "@/components/SiteFooter";
import FooterCTA from "@/components/FooterCTA";

import axios from "axios";
import siteType from "../functions/siteType";

var isUkSite = siteType.lookup();
isUkSite = true;

export default {
  name: "HomePage",
  components: {
    MetaInfo,
    Navigation,
    PageHeader,
    PageHeaderRating,
    SiteFooter,
    FooterCTA,
  },
  data() {
    return {
      activeTestimonial: 1,
      signupFormFirstName: "",
      signupFormEmail: "",
      signupFormError: "",
      themeData: [],
      isUkSite: isUkSite,
      appsData: [],
      homeHeroImage: "home/home-dashboard-screenshot",
      meta: {
        title: "All-in-One Omnichannel Marketing Platform | Transpond",
        description:
          "Try Transpond - Easy to use marketing suite. Create standout communications to send to your customers and grow your business.",
      },
      integrations: [
        {
          name: "Capsule",
          slug: "capsule-email-marketing/8",
          icon: require("../assets/images/home/capsule-icon.svg"),
          description:
            "Automatically have contacts synced in real time between platforms.",
        },
        {
          name: "Zapier",
          slug: "zapier-email-marketing/41",
          icon: require("../assets/images/home/zapier-icon.svg"),
          description:
            "Connect to Zapier and start syncing data between thousands of other apps.",
        },
        {
          name: "Stripe",
          slug: "stripe-email-marketing/15",
          icon: require("../assets/images/home/stripe-icon.svg"),
          description:
            "Connect with Stripe, sync Customers and setup automated emails.",
        },
        {
          name: "Slack",
          slug: "slack-email-marketing/53",
          icon: require("../assets/images/home/slack-icon.svg"),
          description:
            "Automatically send messages to Slack from Campaigns and Automations.",
        },
      ],
    };
  },

  created() {
    axios
      .get("https://api.transpond.io/api/templates?limit=3")
      .then((response) => {
        this.themeData = response.data.Templates;
        return axios.get("https://api.transpond.io/api/applications?limit=10");
      })
      .then((response) => {
        this.appsData = response.data.Applications;
      });
  },
  methods: {
    clickTestimonial(theID) {
      this.activeTestimonial = Number(theID);
    },
    openWindow: function (link, self) {
      window.open(link, self ? "_self" : "_blank");
    },
    clickApp(theApp) {
      this.$router.push({
        path:
          "/integrations/" +
          textHelpers.stringToSlug(theApp.appName + " email marketing") +
          "/" +
          theApp.id + "/",
      });
    },
  },
};
</script>
